<template>
  <div class="fund">
    <div class="fund__container">
      <div class="fund__cover">
        <VideoComponent
          v-if="fundData.cover?.type === 'video'"
          play
          muted
          :poster="fundData.cover.imagePart?.preview || fundDefaultCover"
          :src="fundData.cover.data"
        />
        <img
          v-else
          :src="fundData.cover?.data || fundDefaultCover"
          :alt="`${fundData.name} ${$t('seo.alt.cover')}`"
        />
        <div class="blackout"></div>
        <div class="fund__info">
          <div class="fund__logo">
            <img
              :src="fundData.avatar?.data || fundDefaultLogo"
              :alt="`${fundData.name} ${$t('seo.alt.logo')}`"
            />
          </div>
          <div class="fund__name">
            {{ fundData.name }}
          </div>
          <div class="fund__project-count">
            {{ projectCountText }}
          </div>
        </div>
      </div>
      <div class="card fund__content" v-html="fundDescription" />
    </div>

    <div ref="subscribeButton" class="fund__subscribe-button">
      <div class="fund__subscribe-button-inner">
        <UiButton
          tag="router-link"
          to="/pulse"
          target="_blank"
          @click="savePulseFundPrefillToLocalStorage(fundData, true)"
        >
          {{ $t('button.subscribe_fund') }}
        </UiButton>
      </div>
    </div>

    <!-- <ModalsQrcode /> -->
  </div>
</template>

<script setup>
import fundDefaultCover from '~/assets/images/fund/fundDefaultCover.jpg';
import fundDefaultLogo from '~/assets/images/fund/fundDefaultLogo.png';

import cleanSeoText from '~/utils/cleanSeoText';
import { savePulseFundPrefillToLocalStorage } from '~/utils/pulseFundPrefill';

const { $t } = useI18n();

const { object: fundData } = defineProps({
  id: {
    type: String,
    required: true,
  },
  object: {
    type: Object,
    default: () => ({}),
  },
});
setSeoData();

const subscribeButton = ref(null);

const projectCountText = computed(() => {
  const count = fundData.countActiveProjects ?? 0;
  const key = getWordForm(count, [
    'fund.x1_project_count',
    'fund.x2_project_count',
    'fund.x5_project_count',
  ]);

  return $t(key, { count });
});

const fundDescription = computed(() => {
  if (!fundData) return '';
  let description = fundData.description;
  const videoUrl = fundData.descriptionVideo?.data;
  const videoPreviewUrl = fundData.descriptionVideo?.videoPart?.preview;

  if (videoUrl) {
    description = fundData.description.replace(
      'src="{{description_video}}"',
      `src="${videoUrl}" preload="none"`,
    );

    description = description.replace(/\n/g, ' ').replace(/\r/g, '');
    description = description.replace(
      /(<video\b[^>]*>(.*?)<\/video>)/gi,
      '<div class="fund__video">$1</div>',
    );

    if (videoPreviewUrl) {
      description = description.replace(
        '{{description_video_preview}}',
        videoPreviewUrl,
      );
    } else {
      description = description.replace(
        'poster="{{description_video_preview}}"',
        '',
      );
    }
  }

  description = description.replace(/<p><div/gi, '<div');
  description = description.replace(/div><\/p>/gi, 'div>');

  return fundData.descriptionVideo
    ? description.replace(/<p><\/p>/gi, '')
    : fundData.description;
});

watch(
  () => fundData,
  () => {
    setSeoData();
  },
);

onMounted(() => {
  nextTick(() => {
    document.body.style.paddingBottom =
      subscribeButton.value?.scrollHeight + 'px';
  });
});

function setSeoData() {
  useSeoData().set({
    title: fundData.name,
    description: cleanSeoText(fundData.description),
    image: fundData.cover?.data,
  });
}

function getWordForm(number, forms) {
  const cases = [2, 0, 1, 1, 1, 2];
  const index =
    number % 100 > 4 && number % 100 < 20 ? 2 : cases[Math.min(number % 10, 5)];
  return forms[index];
}
</script>

<style lang="scss">
@use '@styles/components/pages/fund.scss' as *;
</style>
